import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import studentId from "../config";

const Attendance = () => {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const modalRef = useRef(null);

  const [totalPresent, setTotalPresent] = useState(0);
  const [totalAbsent, setTotalAbsent] = useState(0);
  const [totalOnLeave, setTotalOnLeave] = useState(0);

  useEffect(() => {
    const sessionId = localStorage.getItem("currentSessionId");
    const fetchAttendance = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/attendance/getAttendanceByStudentId/${studentId}`,
          { params: { sessionId } }
        );
        setAttendanceData(response.data);
        calculateTotals(response.data);
      } catch (error) {
        console.error("Error fetching attendance data:", error);
      }
    };
    fetchAttendance();
  }, []);

  const calculateTotals = (attendanceData) => {
    let presentCount = 0;
    let absentCount = 0;
    let onLeaveCount = 0;

    attendanceData.forEach((record) => {
      record.students.forEach((student) => {
        if (student.status === "Present") presentCount++;
        else if (student.status === "Absent") absentCount++;
        else if (student.status === "onLeave") onLeaveCount++;
      });
    });

    setTotalPresent(presentCount);
    setTotalAbsent(absentCount);
    setTotalOnLeave(onLeaveCount);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowModal(false);
      }
    };

    if (showModal) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showModal]);

  const generateCalendarDays = () => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayOfWeek = firstDayOfMonth.getDay();
    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const monthDays = [];

    monthDays.push(
      daysOfWeek.map((day, index) => (
        <div
          key={index}
          className="text-center font-semibold text-gray-600 text-[14px]"
        >
          {day}
        </div>
      ))
    );

    for (let i = 0; i < firstDayOfWeek; i++) {
      monthDays.push(<div key={`empty-${i}`} className="border-b"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const isToday =
        new Date().getFullYear() === currentYear &&
        new Date().getMonth() === currentMonth &&
        new Date().getDate() === day;

      const formattedDate = `${currentYear}-${String(currentMonth + 1).padStart(
        2,
        "0"
      )}-${String(day).padStart(2, "0")}`;

      const attendanceRecord = attendanceData.find((record) =>
        record.students.some(
          (student) =>
            new Date(student.date).toLocaleDateString() ===
            new Date(formattedDate).toLocaleDateString()
        )
      );

      let attendanceStatusClass = "";
      if (attendanceRecord) {
        const studentAttendance = attendanceRecord.students.find(
          (student) =>
            new Date(student.date).toLocaleDateString() ===
            new Date(formattedDate).toLocaleDateString()
        );

        if (studentAttendance) {
          if (studentAttendance.status === "Present")
            attendanceStatusClass = "bg-green-600 text-white";
          else if (studentAttendance.status === "Absent")
            attendanceStatusClass = "bg-red-500 text-white";
          else if (studentAttendance.status === "onLeave")
            attendanceStatusClass = "bg-yellow-400 text-white";
          else if (studentAttendance.status === "Late")
            attendanceStatusClass = "bg-orange-500 text-white";
        }
      }

      monthDays.push(
        <div
          key={day}
          className={`text-center py-2 border cursor-pointer rounded-lg transition duration-300 ${
            attendanceStatusClass
          } ${isToday ? "bg-sky-500 text-white" : ""}`}
          onClick={() => handleDateClick(day)}
        >
          {day}
        </div>
      );
    }

    return monthDays;
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDateClick = (day) => {
    const formattedDate = `${currentYear}-${String(currentMonth + 1).padStart(
      2,
      "0"
    )}-${String(day).padStart(2, "0")}`;

    const selectedRecord = attendanceData.find((record) =>
      record.students.some(
        (student) =>
          new Date(student.date).toLocaleDateString() ===
          new Date(formattedDate).toLocaleDateString()
      )
    );

    if (selectedRecord) {
      const studentAttendance = selectedRecord.students.find(
        (student) =>
          new Date(student.date).toLocaleDateString() ===
          new Date(formattedDate).toLocaleDateString()
      );

      setSelectedAttendance(studentAttendance);
      setSelectedDate(
        new Date(currentYear, currentMonth, day).toLocaleDateString()
      );
      setShowModal(true);
    }
  };

  const handleCloseModal = () => setShowModal(false);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const currentMonthName = monthNames[currentMonth];

  return (
    <div className="bg-white p-2 sm:p-4 rounded-lg shadow-md w-full mt-2 max-w-sm sm:max-w-md mx-auto">

      {/* Title - 18px */}
      <div className="text-[18px] font-semibold text-center mb-2">
        Attendance
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <button className="px-2 py-1 bg-gray-200 rounded-lg" onClick={handlePrevMonth}>
          <i className="fas fa-chevron-left text-[14px]"></i>
        </button>

        <div className="text-[18px] font-semibold">
          {currentMonthName} {currentYear}
        </div>

        <button className="px-2 py-1 bg-gray-200 rounded-lg" onClick={handleNextMonth}>
          <i className="fas fa-chevron-right text-[14px]"></i>
        </button>
      </div>

      {/* Calendar */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {generateCalendarDays()}
      </div>

      {/* Summary Cards */}
      <div className="mt-4">
        <h3 className="text-[18px] font-semibold mb-2">Attendance Summary:</h3>

        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 w-full">

          <div className="flex flex-col items-center bg-green-200 p-2 rounded-lg shadow-md w-full sm:flex-1">
            <div className="text-green-600 font-semibold text-[14px]">Present</div>
            <div className="text-green-800 font-bold text-xl">{totalPresent}</div>
          </div>

          <div className="flex flex-col items-center bg-red-200 p-2 rounded-lg shadow-md w-full sm:flex-1">
            <div className="text-red-500 font-semibold text-[14px]">Absent</div>
            <div className="text-red-800 font-bold text-xl">{totalAbsent}</div>
          </div>

          <div className="flex flex-col items-center bg-yellow-200 p-2 rounded-lg shadow-md w-full sm:flex-1">
            <div className="text-yellow-600 font-semibold text-[14px]">On Leave</div>
            <div className="text-yellow-700 font-bold text-xl">{totalOnLeave}</div>
          </div>

        </div>
      </div>

      {/* Legend */}
      <div className="mt-4">
        <h3 className="text-[18px] font-semibold mb-2">Legend:</h3>

        <div className="grid grid-cols-2 gap-4 text-[14px]">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-green-600 mr-2 rounded-full"></div> Present
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-red-500 mr-2 rounded-full"></div> Absent
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-yellow-400 mr-2 rounded-full"></div> On Leave
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-orange-500 mr-2 rounded-full"></div> Late
          </div>
        </div>

        {/* Modal */}
        {showModal && selectedAttendance && (
          <div className="modal fixed inset-0 flex items-center justify-center z-50">
            <div className="modal-overlay absolute inset-0 bg-black opacity-50"></div>

            <div
              ref={modalRef}
              className="modal-container bg-white w-11/12 md:max-w-md mx-auto rounded shadow-lg z-50"
            >
              <div className="modal-content py-4 text-left px-6">

                <div className="flex justify-between items-center pb-3">
                  <p className="text-[18px] font-bold">Attendance Details</p>
                  <button
                    onClick={handleCloseModal}
                    className="modal-close px-3 py-1 rounded-full bg-gray-200"
                  >
                    ✕
                  </button>
                </div>

                <div className="text-[14px] font-semibold mb-4">
                  Date: {selectedDate}
                </div>

                <div className="text-[14px]">
                  <span className="font-bold">Status: </span>
                  <span
                    className={
                      selectedAttendance.status === "Present"
                        ? "text-green-600"
                        : selectedAttendance.status === "Absent"
                        ? "text-red-500"
                        : selectedAttendance.status === "Late"
                        ? "text-orange-500"
                        : "text-yellow-500"
                    }
                  >
                    {selectedAttendance.status}
                  </span>
                </div>

                {selectedAttendance.remark && (
                  <div className="text-[14px] mt-2">
                    <span className="font-bold">Remark: </span>{" "}
                    {selectedAttendance.remark}
                  </div>
                )}

              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Attendance;
